import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { ordersApi, type OrderRow } from '../lib/api'
import { db, ordersOnDay, type Order } from '../lib/db'
import { clock, money, qtyToText } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'
import { OrderDetailSheet } from '../components/OrderDetailSheet'

const toDayInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function HistoryScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const owner = isOwner(useApp((s) => s.session?.role))

  const [day, setDay] = useState(() => toDayInput(new Date()))
  const [query, setQuery] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [remote, setRemote] = useState<OrderRow[] | null>(null)
  const [loading, setLoading] = useState(false)

  /*
   * Hai nguồn, ghép lại:
   *   - Máy này: hiện ra ngay, tra được cả khi mất mạng, và có cả đơn chưa kịp đẩy lên.
   *   - Máy chủ: bổ sung đơn do máy khác trong quán bán.
   *
   * Máy này đứng trước vì nó mới hơn — đơn vừa huỷ xong mà chưa đồng bộ thì bản trên
   * máy mới là bản đúng.
   */
  const local = useLiveQuery(() => ordersOnDay(new Date(day)), [day], [] as Order[])

  useEffect(() => {
    let alive = true
    setLoading(true)
    setRemote(null)

    ordersApi
      .list(day)
      .then((rows) => alive && setRemote(rows))
      .catch(() => alive && setRemote(null))
      .finally(() => alive && setLoading(false))

    return () => {
      alive = false
    }
  }, [day])

  const rows = useMemo(() => {
    const byId = new Map<string, OrderRow>()

    for (const r of remote ?? []) byId.set(r.id, r)

    for (const o of local) {
      byId.set(o.id, {
        id: o.id,
        code: o.code,
        tableName: o.tableName,
        status: o.status,
        paidAt: o.paidAt,
        total: o.total,
        paymentMethod: o.paymentMethod,
        itemCount: o.items.length,
        voidedAt: o.voidedAt,
        deviceId: o.deviceId,
      })
    }

    const q = query.trim().toLowerCase()
    const digits = q.replace(/\D/g, '')

    return [...byId.values()]
      .filter((r) => r.status !== 'Open')
      .filter((r) => {
        if (!q) return true
        return (
          r.code.toLowerCase().includes(q) ||
          (r.tableName ?? '').toLowerCase().includes(q) ||
          (digits.length >= 3 && String(r.total).includes(digits))
        )
      })
      .sort((a, b) => (b.paidAt ?? '').localeCompare(a.paidAt ?? ''))
  }, [remote, local, query])

  const good = rows.filter((r) => !r.voidedAt)
  const revenue = good.reduce((s, r) => s + r.total, 0)
  const voided = rows.length - good.length

  return (
    <div className="screen">
      <AppBar
        title="Hoá đơn"
        sub={
          rows.length === 0
            ? loading
              ? 'Đang tải…'
              : 'Ngày này chưa bán được hoá đơn nào'
            : `${good.length} hoá đơn · ${money(revenue)}đ${voided > 0 ? ` · ${voided} đã huỷ` : ''}`
        }
        onBack={() => nav('/')}
      />

      <div className="body">
        <div className="field-row">
          {owner ? (
            <label className="field">
              <span>Ngày</span>
              <input
                type="date"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                max={toDayInput(new Date())}
              />
            </label>
          ) : (
            <div className="field">
              <span>Ngày</span>
              <div className="pseudo-input">Hôm nay</div>
            </div>
          )}
          <label className="field">
            <span>Tìm</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Số hoá đơn, bàn, số tiền"
              inputMode="search"
            />
          </label>
        </div>

        {remote === null && !loading && (
          <p className="hint">
            Chưa lấy được dữ liệu từ máy chủ — đang xem hoá đơn lưu trên máy này. Hoá đơn do máy
            khác trong quán bán sẽ hiện khi có mạng lại.
          </p>
        )}

        {rows.length === 0 ? (
          <div className="empty">
            <div className="empty-icon" aria-hidden="true">
              🧾
            </div>
            <p>{query ? `Không có hoá đơn nào khớp "${query}".` : 'Ngày này chưa bán hoá đơn nào.'}</p>
          </div>
        ) : (
          <div className="list">
            {rows.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`row-btn bill-row${r.voidedAt ? ' is-void' : ''}`}
                onClick={() => setOpenId(r.id)}
              >
                <span className="row-text">
                  <b>
                    {r.paidAt ? clock(r.paidAt) : '—'}
                    {r.tableName ? ` · ${r.tableName}` : ''}
                  </b>
                  <small>
                    {r.code} · {qtyToText(r.itemCount * 1000)} món
                    {r.paymentMethod === 'Transfer' ? ' · chuyển khoản' : ''}
                  </small>
                </span>
                {r.voidedAt && <span className="diff-tag short">Đã huỷ</span>}
                <span className={`row-price num${r.voidedAt ? ' struck' : ''}`}>{money(r.total)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {openId && (
        <OrderDetailSheet
          orderId={openId}
          onClose={() => setOpenId(null)}
          onVoided={async () => {
            setOpenId(null)
            // Kéo lại từ máy chủ để danh sách khớp với thứ vừa đổi.
            try {
              setRemote(await ordersApi.list(day))
            } catch {
              /* mất mạng thì bản trên máy đã đúng rồi */
            }
            say('Đã huỷ hoá đơn')
          }}
        />
      )}
    </div>
  )
}

/** Dùng ở màn hình khác khi cần mở nhanh hoá đơn cuối cùng. */
export const lastOrder = () => db.orders.orderBy('paidAt').reverse().first()
